<template>
  <div class="container mt-4">
    <div class="card p-4 shadow-sm">
      <template v-if="reserva">
        <!-- HEADER -->
        <div class="d-flex align-items-center mb-4">
          <button class="btn btn-outline-secondary me-3" @click="volver">
            ← Regresar
          </button>
          <h3 class="m-0 flex-grow-1 text-center">Detalles de la reserva</h3>
        </div>

        <!-- HUÉSPED -->
        <h5>🧍 Huésped</h5>
        <ul class="list-group mb-3">
          <li class="list-group-item">
            <strong>Nombre:</strong> {{ reserva.huesped.Nombre }}
            {{ reserva.huesped.Apellido }}
          </li>
          <li class="list-group-item">
            <strong>DPI:</strong> {{ reserva.huesped.DPI || "—" }}
          </li>
          <li class="list-group-item">
            <strong>Teléfono:</strong>
            {{ limpiarNumeroCliente(reserva.huesped.Telefono) }}
          </li>
          <li class="list-group-item">
            <strong>NIT:</strong> {{ reserva.huesped.NIT || "—" }}
          </li>
          <li class="list-group-item">
            <strong>Empresa:</strong> {{ reserva.huesped.Empresa || "—" }}
          </li>
          <li class="list-group-item">
            <strong>Placas:</strong> {{ reserva.huesped.Placas || "—" }}
          </li>
          <li class="list-group-item">
            <strong>Correo:</strong> {{ reserva.huesped.Correo || "—" }}
          </li>
        </ul>

        <!-- FECHAS -->
        <h5>
          📅 Fechas
          <button
            v-if="reserva.EstadoReserva === 0 || reserva.EstadoReserva === 1"
            class="btn btn-sm btn-outline-primary ms-2"
            @click="irAEditarReserva"
            title="Editar fechas y personas"
          >
            ✏️ Editar Reserva
          </button>
        </h5>
        <ul class="list-group mb-4">
          <li class="list-group-item">
            <strong>Fecha Reserva:</strong>
            {{ formatearFecha(reserva.FechaReserva) }}
          </li>
          <li class="list-group-item">
            <strong>Fecha Llegada:</strong>
            {{ formatearFecha(reserva.FechaLlegada) }}
          </li>
          <li class="list-group-item">
            <strong>Fecha Salida:</strong>
            {{ formatearFecha(reserva.FechaSalida) }}
          </li>
        </ul>

        <!-- EMPLEADO -->
        <h5>👤 Empleado que realizó la reserva</h5>
        <ul class="list-group mb-4">
          <li class="list-group-item">
            <strong>Nombre:</strong> {{ reserva.empleado?.Nombres }}
            {{ reserva.empleado?.Apellidos }}
          </li>
        </ul>

        <!-- CONFIRMACIÓN HUÉSPED -->
        <div class="mb-4">
          <span
            v-if="reserva.ConfirmadaPorHuesped"
            class="badge bg-primary fs-6 p-2"
            >✓ Confirmada por huésped</span
          >
          <button
            v-else-if="reserva.EstadoReserva === 0"
            class="badge bg-warning text-dark fs-6 p-2 border-0"
            style="cursor: pointer"
            @click="confirmarPorHuesped"
            title="Clic para marcar como confirmada"
          >
            ⏳ Pendiente de confirmación del huésped
          </button>
        </div>

        <!-- ================= DETALLE POR HABITACIÓN ================= -->
        <h5 class="mt-4">🛏 Detalle por habitación</h5>

        <div
          v-for="h in reserva.habitaciones_reservadas"
          :key="h.IdReservaHabitacion"
          class="card mb-4"
        >
          <!-- HEADER HABITACIÓN -->
          <div
            class="card-header bg-light d-flex justify-content-between align-items-center"
          >
            <div>
              <strong>
                Habitación {{ h.habitacion.Numero }} -
                {{ h.habitacion.tipo.Nombre }}
              </strong>
              <div class="text-muted small">
                Personas: {{ h.CantidadPersonasAlojadas }} | Niños:
                {{ h.CantidadNinios }}
              </div>
            </div>
            <div class="text-end d-flex align-items-center gap-3">
              <div>
                <strong>Precio Habitación</strong><br />
                Q{{ Number(h.TotalHabitacion).toFixed(2) }}
              </div>
              <button
                v-if="
                  reserva.EstadoReserva === 0 || reserva.EstadoReserva === 1
                "
                class="btn btn-sm btn-outline-primary"
                @click="irAEditarReserva"
                title="Editar personas o fechas"
              >
                ✏️ Editar
              </button>
            </div>
          </div>

          <div class="card-body">
            <!-- ================= RESTAURANTE ================= -->
            <h6>🍽 Restaurante</h6>

            <div v-if="h.cuentas_pedido.length" class="table-responsive mb-3">
              <table class="table table-sm table-bordered">
                <thead class="table-secondary text-center">
                  <tr>
                    <th>Platillo</th>
                    <th>Cantidad</th>
                    <th>Precio</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  <template
                    v-for="cuenta in h.cuentas_pedido"
                    :key="cuenta.IdCuentaPedido"
                  >
                    <!-- Nombre cuenta -->
                    <tr class="table-light">
                      <td colspan="4" class="fw-semibold">
                        {{ cuenta.NombreCuenta }}
                      </td>
                    </tr>

                    <!-- Detalles -->
                    <tr v-for="d in cuenta.detalles" :key="d.IdCuentaDetalle">
                      <td>{{ d.detalle_pedido.platillo.Nombre }}</td>
                      <td class="text-center">
                        {{ d.detalle_pedido.Cantidad }}
                      </td>
                      <td class="text-end">
                        Q{{
                          Number(d.detalle_pedido.platillo.Precio).toFixed(2)
                        }}
                      </td>
                      <td class="text-end">
                        Q{{ Number(d.Subtotal).toFixed(2) }}
                      </td>
                    </tr>

                    <!-- Propina -->
                    <tr v-if="Number(cuenta.Propina) > 0" class="table-warning">
                      <td colspan="3" class="text-end fst-italic">
                        Propina ({{ cuenta.NombreCuenta }})
                      </td>
                      <td class="text-end">
                        Q{{ Number(cuenta.Propina).toFixed(2) }}
                      </td>
                    </tr>

                    <!-- Descuento -->
                    <tr
                      v-if="Number(cuenta.SaldoDescuento) > 0"
                      class="table-danger"
                    >
                      <td colspan="3" class="text-end fst-italic">
                        Descuento aplicado
                      </td>
                      <td class="text-end">
                        -Q{{ Number(cuenta.SaldoDescuento).toFixed(2) }}
                      </td>
                    </tr>
                  </template>
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="3" class="text-end">
                      <strong>Total Restaurante</strong>
                    </td>
                    <td class="text-end">
                      <strong>Q{{ h.TotalRestaurante.toFixed(2) }}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <p v-else class="text-muted">Sin consumo en restaurante</p>

            <!-- ================= SERVICIOS EXTRA ================= -->
            <h6 class="mt-3">🧺 Servicios Extra</h6>

            <div v-if="h.servicios_extra.length" class="table-responsive mb-3">
              <table class="table table-sm table-bordered">
                <thead class="table-secondary text-center">
                  <tr>
                    <th>Servicio</th>
                    <th>Cantidad</th>
                    <th>Precio</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <template
                    v-for="venta in h.servicios_extra"
                    :key="venta.IdServicioExtraTotal"
                  >
                    <tr
                      v-for="d in venta.detalles"
                      :key="d.DetalleServicioExtra"
                    >
                      <td>{{ d.servicios_extras.Nombre }}</td>
                      <td class="text-center">{{ d.Cantidad }}</td>
                      <td class="text-end">
                        Q{{ Number(d.servicios_extras.Precio).toFixed(2) }}
                      </td>
                      <td class="text-end">
                        Q{{
                          (d.Cantidad * d.servicios_extras.Precio).toFixed(2)
                        }}
                      </td>
                    </tr>
                  </template>
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="3" class="text-end">
                      <strong>Total Servicios Extra</strong>
                    </td>
                    <td class="text-end">
                      <strong>Q{{ h.TotalServiciosExtra.toFixed(2) }}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <p v-else class="text-muted">Sin servicios extra</p>

            <!-- ================= DESCUENTO HABITACIÓN ================= -->
            <div
              v-if="Number(h.SaldoDescuento) > 0"
              class="alert alert-success text-end mt-3 mb-0"
            >
              <strong>Descuento Aplicado:</strong>
              Q{{ Number(h.SaldoDescuento).toFixed(2) }}
            </div>

            <!-- ================= TOTAL FINAL ================= -->
            <div class="alert alert-info text-end mt-3">
              <strong>Total Consumido Habitación:</strong>
              Q{{
                (
                  Number(h.TotalHabitacion) +
                  Number(h.TotalRestaurante) +
                  Number(h.TotalServiciosExtra)
                ).toFixed(2)
              }}
            </div>
          </div>
        </div>
        <!-- TOTAL / SALDO DE LA RESERVA -->
        <div class="card mt-4 mb-2">
          <div class="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <strong>Total Reserva:</strong>
              Q{{ Number(reserva.TotalReserva || 0).toFixed(2) }}
            </div>
            <div>
              <span v-if="Number(reserva.SaldoPendiente) >= 0">
                <strong>Saldo Pendiente:</strong>
                <span class="text-danger ms-1">Q{{ Number(reserva.SaldoPendiente).toFixed(2) }}</span>
              </span>
              <span v-else>
                <strong>Saldo a Favor:</strong>
                <span class="text-success ms-1">Q{{ Math.abs(Number(reserva.SaldoPendiente)).toFixed(2) }}</span>
              </span>
            </div>
            <button v-if="esAdmin" class="btn btn-sm btn-outline-warning" @click="abrirEditarTotal">
              Editar Total
            </button>
          </div>
        </div>

        <!-- Modal Editar Total (solo Admin) -->
        <div v-if="modalEditarTotal" class="modal fade show d-block" style="background:rgba(0,0,0,0.5)">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">Editar Total de Reserva</h5>
                <button class="btn-close" @click="modalEditarTotal = false"></button>
              </div>
              <div class="modal-body">
                <div class="mb-3">
                  <label class="form-label">Componente a editar</label>
                  <select v-model="formEditarTotal.componente" class="form-select">
                    <option value="hotel">Hospedaje (Hotel)</option>
                    <option value="restaurante">Restaurante</option>
                    <option value="otros">Otros</option>
                  </select>
                </div>
                <div class="mb-3">
                  <label class="form-label">Nuevo Total General (Q)</label>
                  <input type="number" v-model.number="formEditarTotal.nuevoTotal" class="form-control" min="0" step="0.01" />
                  <div class="form-text">Total actual: Q{{ Number(reserva.TotalReserva || 0).toFixed(2) }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label">Justificación <span class="text-danger">*</span></label>
                  <textarea v-model="formEditarTotal.justificacion" class="form-control" rows="3" placeholder="Motivo del ajuste..."></textarea>
                </div>
                <div v-if="formEditarTotal.nuevoTotal !== null" class="alert alert-info py-2 small">
                  Nuevo saldo = Q{{ Number(formEditarTotal.nuevoTotal).toFixed(2) }} - Q{{ (Number(reserva.TotalReserva || 0) - Number(reserva.SaldoPendiente || 0)).toFixed(2) }} (pagado) =
                  <strong :class="(formEditarTotal.nuevoTotal - (Number(reserva.TotalReserva || 0) - Number(reserva.SaldoPendiente || 0))) >= 0 ? 'text-danger' : 'text-success'">
                    Q{{ (formEditarTotal.nuevoTotal - (Number(reserva.TotalReserva || 0) - Number(reserva.SaldoPendiente || 0))).toFixed(2) }}
                  </strong>
                  <span v-if="(formEditarTotal.nuevoTotal - (Number(reserva.TotalReserva || 0) - Number(reserva.SaldoPendiente || 0))) < 0" class="text-success ms-1">(saldo a favor)</span>
                </div>
              </div>
              <div class="modal-footer">
                <button class="btn btn-secondary" @click="modalEditarTotal = false">Cancelar</button>
                <button class="btn btn-warning" @click="guardarEditarTotal" :disabled="guardandoTotal">
                  <span v-if="guardandoTotal" class="spinner-border spinner-border-sm me-1"></span>
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- HISTORIAL DE CAMBIOS DE HABITACIÓN -->
        <div v-if="historialCambios.length > 0" class="mt-4">
          <h5>🔄 Historial de Cambios de Habitación</h5>
          <div class="table-responsive">
            <table class="table table-bordered table-sm text-center align-middle">
              <thead class="table-light">
                <tr>
                  <th>Fecha</th>
                  <th>Hora</th>
                  <th>Habitación Anterior</th>
                  <th>Nueva Habitación</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="cambio in historialCambios" :key="cambio.IdCambioHabitacion">
                  <td>{{ formatearFecha(cambio.Fecha) }}</td>
                  <td>{{ cambio.Hora || '—' }}</td>
                  <td>{{ cambio.HabitacionAnterior }}</td>
                  <td>{{ cambio.NuevaHabitacion }}</td>
                  <td class="text-start">{{ cambio.Motivo }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </template>

      <!-- LOADING -->
      <template v-else>
        <div class="text-center text-muted my-5">
          <div class="spinner-border text-primary"></div>
          <p class="mt-3">Cargando detalles de la reserva...</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_URL } from "@/config/config.js";

export default {
  name: "DetalleReserva",
  data() {
    return {
      reserva: null,
      historialCambios: [],
      modalEditarTotal: false,
      guardandoTotal:   false,
      formEditarTotal:  { componente: 'hotel', nuevoTotal: null, justificacion: '' },
    };
  },
  computed: {
    esAdmin() {
      return parseInt(localStorage.getItem('IdRol') || '0') === 1;
    },
  },
  async mounted() {
    const id = this.$route.params.id;
    const res = await axios.get(`${API_URL}/reservas/detalle/${id}`);
    this.reserva = res.data.reserva;

    try {
      const hist = await axios.get(`${API_URL}/reserva/${id}/historial-cambios`);
      this.historialCambios = hist.data.data;
    } catch (e) {
      this.historialCambios = [];
    }
  },
  methods: {
    async confirmarPorHuesped() {
      if (!confirm("¿Marcar esta reserva como confirmada por el huésped?"))
        return;
      try {
        await axios.put(
          `${API_URL}/reservas/${this.reserva.CodigoCancelacion}/confirmar-publico`,
        );
        this.reserva.ConfirmadaPorHuesped = true;
      } catch (e) {
        alert("No se pudo confirmar la reserva.");
      }
    },
    limpiarNumeroCliente(n) {
      return n ? String(n).replace(/^502/, "") : "";
    },
    formatearFecha(f) {
      if (!f) return "";
      const [y, m, d] = f.split(" ")[0].split("-");
      return `${d}/${m}/${y}`;
    },
    volver() {
      this.$router.push("/reservas");
    },
    irAEditarReserva() {
      this.$router.push({
        name: "ValidarDiaExtraF",
        params: { id: this.reserva.IdReserva },
      });
    },
    abrirEditarTotal() {
      this.formEditarTotal = {
        componente:    'hotel',
        nuevoTotal:    Number(this.reserva.TotalReserva || 0),
        justificacion: '',
      };
      this.modalEditarTotal = true;
    },
    async guardarEditarTotal() {
      if (!this.formEditarTotal.justificacion?.trim()) {
        alert('La justificación es obligatoria.');
        return;
      }
      if (this.formEditarTotal.nuevoTotal === null || this.formEditarTotal.nuevoTotal < 0) {
        alert('El nuevo total debe ser mayor o igual a 0.');
        return;
      }
      this.guardandoTotal = true;
      try {
        const { data } = await axios.put(
          `${API_URL}/reservas/${this.reserva.IdReserva}/editar-total`,
          {
            componente:    this.formEditarTotal.componente,
            nuevoTotal:    this.formEditarTotal.nuevoTotal,
            justificacion: this.formEditarTotal.justificacion,
          }
        );
        this.reserva.TotalReserva   = data.TotalReserva;
        this.reserva.SaldoPendiente = data.SaldoPendiente;
        this.modalEditarTotal = false;
        alert(data.mensaje + (data.saldo_a_favor > 0 ? ` Saldo a favor del huésped: Q${Number(data.saldo_a_favor).toFixed(2)}` : ''));
      } catch (e) {
        alert(e.response?.data?.message || 'Error al editar el total.');
      } finally {
        this.guardandoTotal = false;
      }
    },
  },
};
</script>

<style scoped>
.table th,
.table td {
  vertical-align: middle;
}
</style>
